import { Accordion, Badge, Box, Button, Drawer, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowRight, IconInbox, IconPencil, IconPhoto, IconPlus } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { labelOf } from '../../domain/labels.js';
import { rowLabel } from '../../domain/rowLabel.js';
import { DeleteMasterButton } from '../DeleteMaster/DeleteMasterButton.js';
import { RecordLabel } from '../Diff/RecordLabel.js';
import { TypeIcon } from '../TypeGlyph/TypeIcon.js';
import {
    adoptOneFor,
    assetsRouteFor,
    editRouteFor,
    ENTITY_TYPES,
    newRouteFor,
    type EntityType,
} from '../../domain/entityTypes.js';
import { ENTITY_CALLS } from '../../hooks/entityCalls.js';
import { useAssetStatus } from '../../hooks/useAssets.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { usePull } from '../../hooks/usePull.js';
import { drawerFooter } from './drawerFooter.js';
import { EnvironmentsPanel } from './EnvironmentsPanel.js';
import { FieldDiffPanel } from './FieldDiffPanel.js';
import { MasterSummary } from './MasterSummary.js';
import { useEntityDetail } from './useEntityDetail.js';
import { useFieldAdoption } from '../Adopt/useFieldAdoption.js';

/**
 * Everything known about one entity.
 *
 * A container: it fetches through `useEntityDetail` and arranges three presentational panels.
 * Each panel does one job and can be read on its own — this file used to do all of it, and
 * changing how a diff table looked meant reading the mutation wiring.
 *
 * A stack of cards rather than tabs, because the comparison people need runs ACROSS concerns —
 * you are looking at the master record and the environment's actual value at the same time,
 * and tabs would hide half of every question this drawer exists to answer.
 *
 * The footer acts on the environment the field diff is focused on — **Adopt** and **Push to
 * <env>** (`drawerFooter.ts` decides which) — so the record's detailed diff ends in the two
 * writes it previews, without scrolling back up to the per-environment table's row buttons.
 * It is pinned: the cards scroll above it.
 *
 * The host keys this on `type`/`entityKey`/`focusEnvironment` (see `MatrixOverlays.tsx`,
 * `EnvironmentsPage.tsx`) so a different record, or the same record with a different cell
 * clicked, remounts the drawer instead of carrying its state over — `useEntityDetail`'s own
 * `useState` is then enough, with no `useEffect` chasing the prop a render behind (see
 * `components/Inbox/InboxDrawer.tsx`).
 */

type Props = {
    type: EntityType;
    entityKey: string;
    focusEnvironment?: string;
    onClose: () => void;
    /** Absent where the page cannot push; the Push action is then not offered. */
    onPush?: (environment: string, cell: { type: string; key: string }) => void;
    /** What the page does once the record is deleted from master. */
    onDeleted?: () => void;
};

export const EntityDrawer = ({ type, entityKey, focusEnvironment, onClose, onPush, onDeleted }: Props) => {
    const state = useEntityDetail(type, entityKey, focusEnvironment);
    const fieldAdoption = useFieldAdoption();
    const { pull, pulling } = usePull();
    const assets = useAssetStatus();
    const isMobile = useIsMobile();
    const label = rowLabel(state.title, entityKey);
    const master = state.detail?.master ?? null;
    const inMaster = master !== null;
    const addChild = ENTITY_CALLS[type].addChild;
    /** Gated the same way GameEditorPage gates its own Assets action: the type must offer one, the record must exist in master, and storage must be configured. */
    const assetsHref = inMaster && assets.enabled ? assetsRouteFor(type, entityKey) : null;
    const footer = drawerFooter({ focus: state.focus, status: state.focused?.status, canPush: Boolean(onPush) });

    return (
        <Drawer
            opened
            onClose={onClose}
            position="right"
            size={isMobile ? '100%' : 'xl'}
            styles={{
                title: { flex: 1, minWidth: 0 },
                content: { display: 'flex', flexDirection: 'column' },
                body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: 0 },
            }}
            title={
                <Group gap="xs" wrap="wrap" justify="space-between">
                    <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                        <RecordLabel title={state.title} entityKey={entityKey} layout="inline" size="sm" />
                        <Badge
                            variant="light"
                            color="gray"
                            radius="sm"
                            size="sm"
                            tt="none"
                            leftSection={<TypeIcon type={type} size={14} stroke={1.7} />}
                        >
                            {labelOf(type).singular}
                        </Badge>
                    </Group>
                    {state.detail && (
                        <Group gap="xs" wrap="nowrap">
                            {inMaster ? (
                                <Button
                                    size="compact-sm"
                                    variant="light"
                                    leftSection={<IconPencil size={14} stroke={1.7} />}
                                    component={Link}
                                    to={editRouteFor(type, entityKey)}
                                    onClick={onClose}
                                >
                                    Edit
                                </Button>
                            ) : null}
                            {assetsHref && (
                                <Button
                                    size="compact-sm"
                                    variant="default"
                                    leftSection={<IconPhoto size={14} stroke={1.7} />}
                                    component={Link}
                                    to={assetsHref}
                                    onClick={onClose}
                                >
                                    Assets
                                </Button>
                            )}
                            {!inMaster && (
                                <Button
                                    size="compact-sm"
                                    variant="light"
                                    leftSection={<IconPlus size={14} stroke={1.7} />}
                                    component={Link}
                                    to={newRouteFor(type, entityKey)}
                                    onClick={onClose}
                                >
                                    New {labelOf(type).singular.toLowerCase()}
                                </Button>
                            )}
                            {addChild && inMaster && (
                                <Button
                                    size="compact-sm"
                                    variant="default"
                                    leftSection={<IconPlus size={14} stroke={1.7} />}
                                    component={Link}
                                    to={addChild.route(entityKey)}
                                    onClick={onClose}
                                >
                                    {addChild.label}
                                </Button>
                            )}
                            {master && (
                                <DeleteMasterButton
                                    size="compact-sm"
                                    args={{
                                        type,
                                        key: entityKey,
                                        expectedRevision: master.revision,
                                        title: state.title,
                                        childCount: ENTITY_TYPES[type].childCountOf(state.row),
                                        onDeleted: () => {
                                            onClose();
                                            onDeleted?.();
                                        },
                                    }}
                                />
                            )}
                        </Group>
                    )}
                </Group>
            }
        >
            <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} pb="md">
                {state.isLoading && <Loader />}

                {state.detail && (
                    <Stack gap="md">
                        <Accordion multiple defaultValue={['master', 'environments', 'diff']} variant="separated">
                            <Accordion.Item value="master">
                                <Accordion.Control>Master</Accordion.Control>
                                <Accordion.Panel>
                                    <MasterSummary
                                        type={type}
                                        entityKey={entityKey}
                                        master={state.detail.master}
                                        onClose={onClose}
                                    />
                                </Accordion.Panel>
                            </Accordion.Item>

                            <Accordion.Item value="environments">
                                <Accordion.Control>Per-environment state</Accordion.Control>
                                <Accordion.Panel>
                                    <EnvironmentsPanel
                                        environments={state.detail.environments}
                                        onFocus={state.setFocus}
                                        onPush={
                                            onPush
                                                ? (environment) => onPush(environment, { type, key: entityKey })
                                                : undefined
                                        }
                                        onAdopt={(environment) =>
                                            fieldAdoption.adopt.adopt(adoptOneFor(type, environment, entityKey))
                                        }
                                    />
                                </Accordion.Panel>
                            </Accordion.Item>

                            <Accordion.Item value="diff">
                                <Accordion.Control>
                                    <Group gap="xs">
                                        <span>Field diff</span>
                                        {state.focus && (
                                            <Text className="rgs-numeric" fz="xs" c="dimmed">
                                                {state.focus}
                                            </Text>
                                        )}
                                    </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <FieldDiffPanel
                                        type={type}
                                        environments={state.detail.environments.map((e) => e.name)}
                                        focus={state.focus}
                                        onFocus={state.setFocus}
                                        focused={state.focused}
                                        notCompared={state.detail.notCompared}
                                        onAdoptAllAsOverride={
                                            inMaster
                                                ? (environment, fields) =>
                                                      fieldAdoption.asOverride(type, entityKey, environment, fields)
                                                : undefined
                                        }
                                        rowAction={
                                            inMaster && state.focus
                                                ? fieldAdoption.rowActionFor({
                                                      type,
                                                      key: entityKey,
                                                      environment: state.focus,
                                                      recordLabel: label.label,
                                                  })
                                                : undefined
                                        }
                                        targets={state.detail.master?.targets}
                                        editTargets={
                                            inMaster ? (
                                                <Button
                                                    size="compact-xs"
                                                    variant="subtle"
                                                    component={Link}
                                                    to={editRouteFor(type, entityKey)}
                                                    onClick={onClose}
                                                >
                                                    Edit targets
                                                </Button>
                                            ) : undefined
                                        }
                                        pullNow={
                                            state.focus ? (
                                                <Button
                                                    size="compact-xs"
                                                    variant="subtle"
                                                    loading={pulling.has(state.focus)}
                                                    onClick={() => void pull(state.focus!)}
                                                >
                                                    Pull now
                                                </Button>
                                            ) : undefined
                                        }
                                        onReviewFullScreen={
                                            onPush && state.focus
                                                ? () => onPush(state.focus!, { type, key: entityKey })
                                                : undefined
                                        }
                                    />
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Stack>
                )}
            </Box>
            {footer && (
                <Group
                    gap="xs"
                    wrap="nowrap"
                    justify="flex-end"
                    py="sm"
                    style={{ borderTop: '1px solid var(--rgs-line)', flexShrink: 0 }}
                >
                    {footer.adopt && (
                        <Tooltip label={`${footer.environment} → master`} withArrow>
                            <Button
                                variant="default"
                                leftSection={<IconInbox size={16} stroke={1.7} />}
                                style={{ flex: isMobile ? 1 : undefined }}
                                onClick={() =>
                                    fieldAdoption.adopt.adopt(adoptOneFor(type, footer.environment, entityKey))
                                }
                            >
                                Adopt
                            </Button>
                        </Tooltip>
                    )}
                    {footer.push && (
                        <Tooltip label={`master → ${footer.environment}`} withArrow>
                            <Button
                                leftSection={<IconArrowRight size={16} stroke={1.7} />}
                                style={{ flex: isMobile ? 1 : undefined }}
                                onClick={() => onPush!(footer.environment, { type, key: entityKey })}
                            >
                                <span>
                                    Push to <span className="rgs-numeric">{footer.environment}</span>
                                </span>
                            </Button>
                        </Tooltip>
                    )}
                </Group>
            )}
            {fieldAdoption.dialog}
        </Drawer>
    );
};
